import {isValidElement, cloneElement, JSX, ReactNode} from 'react';

type MessageArg = JSX.Element | HTMLElement | ReactNode;

const getMessageCode = function getMessageCode(
	messages: Record<string, string> | Map<string, string>,
	code: string
): string | undefined {
	if (!messages || typeof messages !== 'object') return undefined;

	if ('get' in messages && typeof messages.get === 'function') {
		return messages.get(code);
	}

	return (messages as Record<string, string>)[code];
};

export function getMessage<
	Messages extends Record<string, string>,
	MessageCode extends string,
	MessageArgs extends MessageArg[]
>(messages: Messages, code: MessageCode, args?: MessageArgs): ReactNode {
	const message = getMessageCode(messages, code);
	if (!args || !message) return message || code;

	// turn "foo {0} bar" into ['foo ', '0', ' bar']
	const messageParts = message.split(/{(\d*)}/);
	let hasElement = false;

	const mappedParts = messageParts.map((str, i): ReactNode => {
		// every second item should be replaced with an arg
		if (i % 2 === 0) return str;

		let replacement = args[Number(str)];
		if (replacement === null || replacement === undefined) {
			return `{${str}}`;
		}

		if (isValidElement(replacement)) {
			replacement = cloneElement(replacement, {key: str});
			hasElement = true;
		}

		return replacement as ReactNode;
	});

	return hasElement ? mappedParts : (mappedParts as string[]).join('');
}

export type LocaleDirection = 'rtl' | 'ltr';

export function getLocaleDirection(locale: string): LocaleDirection {
	return locale === 'ar' ? 'rtl' : 'ltr';
}
